import { Fragment, useEffect } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { X } from 'lucide-react'
import { toast } from 'react-toastify'
import { agentsAPI } from '../services/api'
import LoadingSpinner from './LoadingSpinner'

function AgentModal({ isOpen, onClose, agent }) {
  const queryClient = useQueryClient()
  const isEditing = !!agent

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm()

  const { data: voices, isLoading: voicesLoading } = useQuery(
    'voices',
    agentsAPI.getVoices,
    { enabled: isOpen }
  )

  const createMutation = useMutation(agentsAPI.createAgent, {
    onSuccess: () => {
      toast.success('Agent created successfully')
      queryClient.invalidateQueries('agents')
      onClose()
      reset()
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create agent')
    }
  })

  const updateMutation = useMutation(
    ({ id, data }) => agentsAPI.updateAgent(id, data),
    {
      onSuccess: () => {
        toast.success('Agent updated successfully')
        queryClient.invalidateQueries('agents')
        onClose()
        reset()
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update agent')
      }
    }
  )

  useEffect(() => {
    if (agent) {
      reset({
        name: agent.name,
        description: agent.description || '',
        voiceId: agent.voiceId,
        prompt: agent.prompt || '',
        firstMessage: agent.firstMessage || '',
        language: agent.language || 'en'
      })
    } else {
      reset({
        name: '',
        description: '',
        voiceId: '',
        prompt: '',
        firstMessage: '',
        language: 'en'
      })
    }
  }, [agent, reset])

  const onSubmit = (data) => {
    if (isEditing) {
      updateMutation.mutate({ id: agent._id, data })
    } else {
      createMutation.mutate(data)
    }
  }

  const isLoading = createMutation.isLoading || updateMutation.isLoading

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-primary-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-primary-400 hover:text-primary-500"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-primary-900">
                      {isEditing ? 'Edit Agent' : 'Create New Agent'}
                    </Dialog.Title>

                    <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-primary-700 mb-1">
                          Name *
                        </label>
                        <input
                          {...register('name', { required: 'Name is required' })}
                          type="text"
                          className="input-field"
                          placeholder="Agent name"
                        />
                        {errors.name && (
                          <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-primary-700 mb-1">
                          Description
                        </label>
                        <textarea
                          {...register('description')}
                          rows={3}
                          className="input-field"
                          placeholder="Agent description"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-primary-700 mb-1">
                          Voice *
                        </label>
                        {voicesLoading ? (
                          <div className="flex items-center justify-center py-2">
                            <LoadingSpinner size="sm" />
                          </div>
                        ) : (
                          <select
                            {...register('voiceId', { required: 'Voice is required' })}
                            className="input-field"
                          >
                            <option value="">Select a voice</option>
                            {voices?.data?.voices?.map((voice) => (
                              <option key={voice.voice_id} value={voice.voice_id}>
                                {voice.name} ({voice.labels?.gender}, {voice.labels?.age})
                              </option>
                            ))}
                          </select>
                        )}
                        {errors.voiceId && (
                          <p className="mt-1 text-sm text-red-600">{errors.voiceId.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-primary-700 mb-1">
                          System Prompt
                        </label>
                        <textarea
                          {...register('prompt')}
                          rows={4}
                          className="input-field"
                          placeholder="Instructions for the AI agent..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-primary-700 mb-1">
                          First Message
                        </label>
                        <textarea
                          {...register('firstMessage')}
                          rows={2}
                          className="input-field"
                          placeholder="Initial greeting message"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-primary-700 mb-1">
                          Language
                        </label>
                        <select
                          {...register('language')}
                          className="input-field"
                        >
                          <option value="en">English</option>
                          <option value="ar">Arabic</option>
                        </select>
                      </div>

                      <div className="mt-6 flex justify-end space-x-3">
                        <button
                          type="button"
                          onClick={onClose}
                          className="btn-secondary"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={isLoading}
                          className="btn-primary"
                        >
                          {isLoading && <LoadingSpinner size="sm" className="mr-2" />}
                          {isLoading ? 'Saving...' : isEditing ? 'Update' : 'Create'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export default AgentModal